import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { FullScreenLoader } from '@/components/feedback/FullScreenLoader';
import AuthenticatedLayout from './RouterLayouts/AuthenticatedLayout';
import GuestLayout from './RouterLayouts/GuestLayout';
import SuspenseOutlet from './RouterLayouts/SuspenseOutlet';
import TripMapRedirect from './RouterLayouts/TripMapRedirect';

// ---------------------------------------------------------------------------
// Lazy-loaded page components
// ---------------------------------------------------------------------------

// Auth
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const OnboardingPage = lazy(() => import('@/features/onboarding/pages/OnboardingPage'));
const PrivacyPolicyPage = lazy(() => import('@/features/legal/pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('@/features/legal/pages/TermsPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));

// Trips
const TripsListPage = lazy(() => import('@/features/trips/pages/TripsListPage'));
const CreateTripPage = lazy(() => import('@/features/trips/pages/CreateTripPage'));
const TripDetailPage = lazy(() => import('@/features/trips/pages/TripDetailPage'));
const TripPlanPage = lazy(() => import('@/features/trips/pages/TripPlanPage'));
const TripDayPage = lazy(() => import('@/features/trips/pages/TripDayPage'));
const TravelPointDetailPage = lazy(() => import('@/features/trips/pages/TravelPointDetailPage'));
const TripSettingsPage = lazy(() => import('@/features/trips/pages/TripSettingsPage'));

// Checklist
const TripChecklistPage = lazy(() => import('@/features/checklist/pages/TripChecklistPage'));

// Expenses
const TripExpensesPage = lazy(() => import('@/features/expenses/pages/TripExpensesPage'));
const CreateExpensePage = lazy(() => import('@/features/expenses/pages/CreateExpensePage'));
const ExpenseDetailPage = lazy(() => import('@/features/expenses/pages/ExpenseDetailPage'));

// Photos
const TripPhotosPage = lazy(() => import('@/features/photos/pages/TripPhotosPage'));

// Trip Feed / Journal
const FeedTripPage = lazy(() => import('@/pages/FeedTripPage'));

// AI
const TripSummaryPage = lazy(() => import('@/features/ai/pages/TripSummaryPage'));
const AiPlannerPage = lazy(() => import('@/features/ai/pages/AiPlannerPage'));

// Social
const FollowListPage = lazy(() => import('@/features/inspirations/pages/FollowListPage'));
const NotificationsPage = lazy(() => import('@/features/social/pages/NotificationsPage'));

// Profile & Maps
const UserProfilePage = lazy(() => import('@/features/social/pages/UserProfilePage'));
const PublicProfilePage = lazy(() => import('@/features/profile/pages/PublicProfilePage'));
const MapPage = lazy(() => import('@/features/maps/pages/MapPage'));

// Settings
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));

// Error
const NotFoundPage = lazy(() => import('@/app/NotFoundPage'));
const InspirationPage = lazy(() => import('@/features/inspirations/pages/InspirationPage'));
const ComposerPage = lazy(() => import('@/features/inspirations/pages/ComposerPage'));
const HomePage = lazy(() => import('@/features/inspirations/pages/HomePage'));
const SearchPage = lazy(() => import('@/features/inspirations/pages/SearchPage'));
const BoardPage = lazy(() => import('@/features/inspirations/pages/BoardPage'));
const PlacePage = lazy(() => import('@/features/inspirations/pages/PlacePage'));

// ---------------------------------------------------------------------------
// Router definition
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  // -------------------------------------------------------------------------
  // Guest routes (login, register, password reset)
  // -------------------------------------------------------------------------
  {
    element: <GuestLayout />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/register',
        element: <RegisterPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
    ],
  },

  // -------------------------------------------------------------------------
  // Authenticated routes (main app)
  // -------------------------------------------------------------------------
  {
    element: <AuthenticatedLayout />,
    children: [
      // -------------------------------------------------------------------
      // Home — the trip list. There is no global wall: conversations live
      // inside a trip, so the home tab is the list of trips.
      // -------------------------------------------------------------------
      // Home is the explore feed (docs/INSPIRATIONS.md, stage 3); the trip
      // list has its own tab at /trips.
      {
        index: true,
        path: '/',
        element: <HomePage />,
      },
      {
        path: '/search',
        element: <SearchPage />,
      },
      {
        path: '/welcome',
        element: <OnboardingPage />,
      },

      // -------------------------------------------------------------------
      // Trips
      // -------------------------------------------------------------------
      {
        path: '/trips',
        element: <SuspenseOutlet />,
        children: [
          {
            index: true,
            element: <TripsListPage />,
          },
          {
            path: 'new',
            element: <CreateTripPage />,
          },
          {
            path: ':tripId',
            element: <SuspenseOutlet />,
            children: [
              {
                index: true,
                element: <TripDetailPage />,
              },
              // Trip Plan (day-by-day itinerary)
              {
                path: 'plan',
                element: <SuspenseOutlet />,
                children: [
                  {
                    index: true,
                    element: <TripPlanPage />,
                  },
                  {
                    path: ':dayId',
                    element: <SuspenseOutlet />,
                    children: [
                      {
                        index: true,
                        element: <TripDayPage />,
                      },
                      {
                        path: ':pointId',
                        element: <TravelPointDetailPage />,
                      },
                    ],
                  },
                ],
              },
              // Trip Map
              {
                path: 'map',
                element: <TripMapRedirect />,
              },
              // Checklist
              {
                path: 'checklist',
                element: <TripChecklistPage />,
              },
              // Expenses
              {
                path: 'expenses',
                element: <SuspenseOutlet />,
                children: [
                  {
                    index: true,
                    element: <TripExpensesPage />,
                  },
                  {
                    path: 'new',
                    element: <CreateExpensePage />,
                  },
                  {
                    path: ':expenseId',
                    element: <ExpenseDetailPage />,
                  },
                  {
                    path: ':expenseId/edit',
                    element: <CreateExpensePage />,
                  },
                ],
              },
              // Photos
              {
                path: 'photos',
                element: <TripPhotosPage />,
              },
              // Trip Feed / Journal
              {
                path: 'feed',
                element: <FeedTripPage />,
              },
              // AI Trip Summary
              {
                path: 'summary',
                element: <TripSummaryPage />,
              },
              // Trip Settings (members, visibility, danger zone)
              {
                path: 'settings',
                element: <TripSettingsPage />,
              },
            ],
          },
        ],
      },

      // -------------------------------------------------------------------
      // AI Planner (standalone, not trip-specific)
      // -------------------------------------------------------------------
      {
        path: '/ai/plan',
        element: <AiPlannerPage />,
      },

      // -------------------------------------------------------------------
      // Social
      // -------------------------------------------------------------------
      {
        // Friends became follows (ADR-0007); old links and notifications land on the profile.
        path: '/friends',
        element: <Navigate to="/profile" replace />,
      },
      {
        path: '/notifications',
        element: <NotificationsPage />,
      },

      // -------------------------------------------------------------------
      // Personal Map (top-level, for current user)
      // -------------------------------------------------------------------
      {
        path: '/map',
        element: <MapPage />,
      },

      // -------------------------------------------------------------------
      // Current User Profile (top-level, no userId needed)
      // -------------------------------------------------------------------
      {
        path: '/profile',
        element: <UserProfilePage />,
      },

      // -------------------------------------------------------------------
      // User Profile & Personal Map (other users, by userId)
      // -------------------------------------------------------------------
      {
        path: '/profile/:userId',
        element: <SuspenseOutlet />,
        children: [
          {
            // Not UserProfilePage. That one reads the signed-in user out of the
            // store, so somebody else's profile showed you your own — email,
            // sign-out button and all.
            index: true,
            element: <PublicProfilePage />,
          },
          {
            path: 'map',
            element: <MapPage />,
          },
          {
            path: 'followers',
            element: <FollowListPage direction="followers" />,
          },
          {
            path: 'following',
            element: <FollowListPage direction="following" />,
          },
        ],
      },

      // -------------------------------------------------------------------
      // Inspirations (docs/INSPIRATIONS.md)
      // -------------------------------------------------------------------
      {
        path: '/inspirations',
        element: <SuspenseOutlet />,
        children: [
          { path: 'new', element: <ComposerPage /> },
          { path: ':id', element: <InspirationPage /> },
          { path: ':id/edit', element: <ComposerPage /> },
        ],
      },
      {
        path: '/boards/:id',
        element: <SuspenseOutlet />,
        children: [{ index: true, element: <BoardPage /> }],
      },
      {
        path: '/places/:id',
        element: <SuspenseOutlet />,
        children: [{ index: true, element: <PlacePage /> }],
      },

      // -------------------------------------------------------------------
      // Settings
      // -------------------------------------------------------------------
      {
        path: '/settings',
        element: <SettingsPage />,
      },
    ],
  },

  // -------------------------------------------------------------------------
  // Fallback routes
  // -------------------------------------------------------------------------
  // Shared-link address (ADR-0004): no shell and no guard, so somebody without
  // an account sees the Inspiration and an "open in the app" prompt; a
  // signed-in user gets the same page with the actions that need them.
  {
    path: '/i/:id',
    element: <SuspenseOutlet />,
    children: [{ index: true, element: <InspirationPage publicMode /> }],
  },
  {
    path: '/p/:id',
    element: <SuspenseOutlet />,
    children: [{ index: true, element: <PlacePage publicMode /> }],
  },
  {
    path: '/u/:handle',
    element: <SuspenseOutlet />,
    children: [{ index: true, element: <PublicProfilePage publicMode /> }],
  },

  // Public: the app stores and the sign-up screen link to these.
  {
    path: '/privacy',
    element: <SuspenseOutlet />,
    children: [{ index: true, element: <PrivacyPolicyPage /> }],
  },
  {
    path: '/terms',
    element: <SuspenseOutlet />,
    children: [{ index: true, element: <TermsPage /> }],
  },

  {
    path: '/404',
    element: (
      <Suspense fallback={<FullScreenLoader />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
]);
